import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/admin/AdminLayout";
import KPICard from "@/components/admin/KPICard";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { 
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue
} from "@/components/ui/BrutalSelect";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { Loader2, Search, Eye, Pause, Play, Trash2, ArrowLeft, Briefcase, Flag } from "lucide-react";
import { ContentReportModal } from "@/components/admin/ContentReportModal";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface JobPosting {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'active' | 'paused' | 'expired' | 'removed';
  contract_type: string;
  work_mode: string;
  location_city: string;
  created_at: string;
  published_at: string | null;
  expires_at: string | null;
  total_views: number;
  total_clicks: number;
  company_id: string;
  company?: {
    name: string;
    email: string | null;
    owner_user_id: string;
  };
}

export default function AdminJobs() {
  const { isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);

  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobPosting[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [recentFilter, setRecentFilter] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalViews: 0,
    totalClicks: 0,
  });

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'it': return it;
      case 'es': return es;
      default: return enUS;
    }
  };

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      navigate("/");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    loadJobs();
    loadReportCounts();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [searchQuery, statusFilter, recentFilter, jobs, reportCounts]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const { data: jobsData, error: jobsError } = await supabase
        .from("job_postings")
        .select("*")
        .order("created_at", { ascending: false });

      if (jobsError) throw jobsError;

      // Get unique company IDs
      const companyIds = [...new Set(jobsData?.map(j => j.company_id).filter(Boolean))];
      
      // Fetch company data
      const { data: companiesData, error: companiesError } = await supabase
        .from("companies")
        .select("id, name, email, owner_user_id")
        .in("id", companyIds);

      if (companiesError) throw companiesError;

      // Map companies to jobs
      const companiesMap = new Map(companiesData?.map(c => [c.id, c]) || []);
      const jobsWithCompanies = jobsData?.map(job => ({
        ...job,
        company: companiesMap.get(job.company_id)
      })) || [];

      setJobs(jobsWithCompanies);
      calculateStats(jobsWithCompanies);
    } catch (error) {
      console.error("Error loading jobs:", error);
      toast({
        title: t("common.error"),
        description: t("admin.jobs.messages.loadError"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: JobPosting[]) => {
    const active = data.filter((j) => j.status === "active").length;
    const views = data.reduce((sum, j) => sum + (j.total_views || 0), 0);
    const clicks = data.reduce((sum, j) => sum + (j.total_clicks || 0), 0);

    setStats({
      totalJobs: data.length,
      activeJobs: active,
      totalViews: views,
      totalClicks: clicks,
    });
  };

  const loadReportCounts = async () => {
    try {
      const { data } = await supabase
        .from("user_reports")
        .select("reported_job_id")
        .eq("status", "pending")
        .not("reported_job_id", "is", null);

      const counts: Record<string, number> = {};
      data?.forEach((r: any) => {
        counts[r.reported_job_id] = (counts[r.reported_job_id] || 0) + 1;
      });
      setReportCounts(counts);
    } catch (error) {
      console.error("Error loading report counts:", error);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    if (statusFilter === "flagged") {
      filtered = filtered.filter((j) => (reportCounts[j.id] ?? 0) > 0);
    } else if (statusFilter !== "all") {
      filtered = filtered.filter((j) => j.status === statusFilter);
    }

    if (recentFilter) {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      filtered = filtered.filter((j) => new Date(j.created_at) > oneDayAgo);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (j) =>
          j.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          j.location_city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          j.company?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  const toggleStatus = async (jobId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      
      const { error } = await supabase
        .from("job_postings")
        .update({ status: newStatus })
        .eq("id", jobId);

      if (error) throw error;

      toast({
        title: t("admin.jobs.messages.operationComplete"),
        description: newStatus === 'active' ? t("admin.jobs.messages.activateSuccess") : t("admin.jobs.messages.pauseSuccess"),
      });

      loadJobs();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast({
        title: t("common.error"),
        description: t("admin.jobs.messages.statusError"),
        variant: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (!jobToDelete) return;

    try {
      const { error } = await supabase.from("job_postings").delete().eq("id", jobToDelete);

      if (error) throw error;

      toast({
        title: t("admin.jobs.messages.operationComplete"),
        description: t("admin.jobs.messages.deleteSuccess"),
      });

      loadJobs();
    } catch (error) {
      console.error("Error deleting job:", error);
      toast({
        title: t("common.error"),
        description: t("admin.jobs.messages.deleteError"),
        variant: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'paused':
        return 'bg-orange-100 text-orange-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status: string) => {
    const key = `admin.jobs.status.${status}`;
    const label = t(key);
    return label === key ? status : label;
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.jobs.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.jobs.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Briefcase className="w-10 h-10 text-blue-600" />
              {t("admin.jobs.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.jobs.subtitle")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <KPICard title={t("admin.jobs.kpi.totalJobs")} value={stats.totalJobs} />
          <KPICard title={t("admin.jobs.kpi.activeJobs")} value={stats.activeJobs} />
          <KPICard title={t("admin.jobs.kpi.totalViews")} value={stats.totalViews} />
          <KPICard title={t("admin.jobs.kpi.totalClicks")} value={stats.totalClicks} />
        </div>

        <BrutalCard>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <BrutalInput
                  placeholder={t("admin.jobs.filters.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <BrutalSelect value={statusFilter} onValueChange={setStatusFilter}>
                <BrutalSelectTrigger className="w-[200px]">
                  <BrutalSelectValue placeholder={t("admin.jobs.filters.statusPlaceholder")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  <BrutalSelectItem value="all">{t("admin.jobs.filters.all")}</BrutalSelectItem>
                  <BrutalSelectItem value="active">{t("admin.jobs.filters.active")}</BrutalSelectItem>
                  <BrutalSelectItem value="draft">{t("admin.jobs.filters.draft")}</BrutalSelectItem>
                  <BrutalSelectItem value="paused">{t("admin.jobs.filters.paused")}</BrutalSelectItem>
                  <BrutalSelectItem value="expired">{t("admin.jobs.filters.expired")}</BrutalSelectItem>
                  <BrutalSelectItem value="flagged">{t("admin.jobs.filters.flagged")}</BrutalSelectItem>
                </BrutalSelectContent>
              </BrutalSelect>
              <label className="flex items-center gap-2 border-[3px] border-dark px-4 py-2 bg-card cursor-pointer">
                <input
                  type="checkbox"
                  checked={recentFilter}
                  onChange={(e) => setRecentFilter(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="font-heading font-bold text-sm">{t("admin.jobs.filters.last24h")}</span>
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b-[3px] border-dark">
                  <tr>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.title")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.company")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.contract")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.workMode")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.city")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.publishedAt")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.expiresAt")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.stats")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.status")}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.jobs.table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-muted-foreground">
                        {t("admin.jobs.table.noResults")}
                      </td>
                    </tr>
                  ) : (
                    filteredJobs.map((job) => {
                      const isNew = (new Date().getTime() - new Date(job.created_at).getTime()) < 86400000;
                      return (
                        <tr key={job.id} className="border-b-[3px] border-dark hover:bg-surface">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {isNew && <BrutalBadge variant="warning">NEW</BrutalBadge>}
                              {reportCounts[job.id] > 0 && (
                                <BrutalBadge variant="secondary" className="flex items-center gap-1">
                                  <Flag className="w-3 h-3" /> {reportCounts[job.id]}
                                </BrutalBadge>
                              )}
                              <span className="font-bold">{job.title}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <p className="font-bold">{job.company?.name || "N/D"}</p>
                              <p className="text-xs text-muted-foreground">{job.company?.email || ""}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 uppercase text-xs">{job.contract_type}</td>
                          <td className="px-6 py-4 uppercase text-xs">{job.work_mode}</td>
                          <td className="px-6 py-4">{job.location_city || "N/D"}</td>
                          <td className="px-6 py-4">
                            {job.published_at 
                              ? format(new Date(job.published_at), "dd/MM/yyyy", { locale: getDateLocale() })
                              : "—"
                            }
                          </td>
                          <td className="px-6 py-4">
                            {job.expires_at 
                              ? format(new Date(job.expires_at), "dd/MM/yyyy", { locale: getDateLocale() })
                              : "—"
                            }
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs">
                              <span className="font-bold">{job.total_views || 0}</span> {t("admin.jobs.table.views")}
                              <br />
                              <span className="font-bold">{job.total_clicks || 0}</span> {t("admin.jobs.table.clicks")}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-bold uppercase ${getStatusBadgeClass(job.status)}`}>
                              {getStatusLabel(job.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              {reportCounts[job.id] > 0 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => { setSelectedJob(job); setReportModalOpen(true); }}
                                        className="p-2 border-[3px] border-dark bg-destructive text-destructive-foreground shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                        aria-label={t("admin.jobs.actions.viewReports")}
                                      >
                                        <Flag className="w-4 h-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{t("admin.jobs.actions.viewReports")} ({reportCounts[job.id]})</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => window.open(`/jobs/${job.slug}`, '_blank')}
                                      className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                      aria-label={t("admin.jobs.actions.view")}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t("admin.jobs.actions.view")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => toggleStatus(job.id, job.status)}
                                      className={`p-2 border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all ${
                                        job.status === 'active' ? 'bg-orange-400 text-dark' : 'bg-primary text-dark'
                                      }`}
                                      aria-label={job.status === 'active' ? t("admin.jobs.actions.pause") : t("admin.jobs.actions.activate")}
                                    >
                                      {job.status === 'active' ? (
                                        <Pause className="w-4 h-4" />
                                      ) : (
                                        <Play className="w-4 h-4" />
                                      )}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{job.status === 'active' ? t("admin.jobs.actions.pause") : t("admin.jobs.actions.activate")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => {
                                        setJobToDelete(job.id);
                                        setDeleteDialogOpen(true);
                                      }}
                                      className="p-2 border-[3px] border-dark bg-destructive text-destructive-foreground shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                      aria-label={t("admin.jobs.actions.delete")}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t("admin.jobs.actions.delete")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </BrutalCard>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.jobs.deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.jobs.deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedJob && (
        <ContentReportModal
          isOpen={reportModalOpen}
          onClose={() => { setReportModalOpen(false); setSelectedJob(null); }}
          contentType="job"
          contentId={selectedJob.id}
          contentTitle={selectedJob.title}
          onAction={() => { loadJobs(); loadReportCounts(); }}
        />
      )}
    </AdminLayout>
  );
}
